import React, { useMemo } from "react";
import ReactSelect from "react-select";

const Select = ({
  label,
  name,
  children,
  options,
  onChange,
  className,
  innerClassName,
  value,
  error,
  disabled = false,
  slug = false,
  isSearchable = true,
  placeholder,
  ...rest
}) => {
  // Convert children (option elements) to options array format for react-select
  const selectOptions = useMemo(() => {
    if (options) {
      return options;
    }
    if (children) {
      const childArray = React.Children.toArray(children);
      return childArray
        .filter((child) => child.type === "option")
        .map((child) => ({
          value: child.props.value,
          label: child.props.children || child.props.value,
        }));
    }
    return [];
  }, [children, options]);

  // Find the selected option based on value
  const selectedOption = useMemo(() => {
    if (value === undefined || value === null || value === "") {
      return null;
    }
    return selectOptions.find((opt) => opt.value === value || String(opt.value) === String(value));
  }, [value, selectOptions]);

  // Handle onChange to maintain backward compatibility with event object
  const handleChange = (selectedOption) => {
    if (onChange) {
      // Create a synthetic event object to maintain backward compatibility
      const syntheticEvent = {
        target: {
          name: name,
          value: selectedOption ? selectedOption.value : "",
        },
      };
      onChange(syntheticEvent);
    }
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: disabled ? "#f9fafb" : "white",
      borderColor: error
        ? "#ef4444"
        : state.isFocused
        ? "#6366f1"
        : "#d1d5db",
      borderWidth: "1px",
      borderRadius: slug ? "0.125rem" : "0.375rem",
      boxShadow: state.isFocused && !error ? "0 0 0 1px #6366f1" : "none",
      minHeight: "38px",
      "&:hover": {
        borderColor: error ? "#ef4444" : state.isFocused ? "#6366f1" : "#d1d5db",
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#111827",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#6366f1"
        : state.isFocused
        ? "#f3f4f6"
        : "white",
      color: state.isSelected ? "white" : "#111827",
      cursor: "pointer",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9ca3af",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  return (
    <div className={className}>
      <label className="block font-bold title text-warmgray-900">{label}</label>
      <div
        className={
          (slug
            ? "flex flex-row w-full focus:outline-none mt-3"
            : "flex flex-row w-full focus:outline-none") +
          (disabled ? " opacity-60" : "") +
          (" " + innerClassName)
        }
      >
        {slug ? (
          <div className="bg-warmgray-200 p-3 border border-r-0 border-warmgray-300 rounded-l-md">
            <span className="self-center">{slug}</span>
          </div>
        ) : (
          ""
        )}
        <div className={slug ? "flex-1" : "w-full"}>
          <ReactSelect
            name={name}
            value={selectedOption}
            options={selectOptions}
            onChange={handleChange}
            isDisabled={disabled}
            isSearchable={isSearchable}
            placeholder={placeholder || "Select..."}
            styles={customStyles}
            className="w-full"
            classNamePrefix="select"
            {...rest}
          />
        </div>
      </div>
      {error ? (
        <span className="block text-red-500">
          <small>{error}</small>
        </span>
      ) : (
        ""
      )}
    </div>
  );
};

export default Select;
